import React, { useState, useEffect } from 'react'
import { api } from '../api'
import { useAuth } from '../App'

const ALL_TABS = ['Dashboard', 'Work Items', 'Executive Tracker', 'Members', 'Feature Parity']

function Admin() {
  const auth = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [form, setForm] = useState({ githubUsername: '', role: 'user', allowedTabs: ['Dashboard', 'Work Items', 'Executive Tracker'] })

  const loadUsers = () => {
    setLoading(true)
    api.getUsers()
      .then(setUsers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadUsers() }, [])

  if (!auth?.isAdmin) return <div className="page"><h2>Access Denied</h2></div>

  const handleTabToggle = (tab) => {
    setForm(f => ({
      ...f,
      allowedTabs: f.allowedTabs.includes(tab)
        ? f.allowedTabs.filter(t => t !== tab)
        : [...f.allowedTabs, tab]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, { role: form.role, allowedTabs: form.allowedTabs })
      } else {
        await api.createUser(form)
      }
      setShowForm(false)
      setEditingUser(null)
      setForm({ githubUsername: '', role: 'user', allowedTabs: ['Dashboard', 'Work Items', 'Executive Tracker'] })
      loadUsers()
    } catch (e) {
      setError(e.message)
    }
  }

  const handleEdit = (user) => {
    setEditingUser(user)
    setForm({ githubUsername: user.githubUsername, role: user.role, allowedTabs: [...user.allowedTabs] })
    setShowForm(true)
  }

  const handleDelete = async (user) => {
    if (!confirm(`Remove ${user.githubUsername} from whitelist?`)) return
    try {
      await api.deleteUser(user.id)
      loadUsers()
    } catch (e) {
      setError(e.message)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingUser(null)
    setForm({ githubUsername: '', role: 'user', allowedTabs: ['Dashboard', 'Work Items', 'Executive Tracker'] })
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>⚙️ User Management</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Add User</button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <h3>{editingUser ? 'Edit User' : 'Add User'}</h3>
          <div className="form-group">
            <label>GitHub Username</label>
            <input
              type="text"
              value={form.githubUsername}
              onChange={e => setForm(f => ({ ...f, githubUsername: e.target.value }))}
              disabled={!!editingUser}
              required
              placeholder="github-username"
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <select value={form.role} onChange={e => {
              const role = e.target.value
              setForm(f => ({ ...f, role, allowedTabs: role === 'admin' ? [...ALL_TABS] : f.allowedTabs }))
            }}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="form-group">
            <label>Allowed Tabs</label>
            <div className="tab-checkboxes">
              {ALL_TABS.map(tab => (
                <label key={tab} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.allowedTabs.includes(tab)}
                    onChange={() => handleTabToggle(tab)}
                  />
                  {tab}
                </label>
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">{editingUser ? 'Update' : 'Add'}</button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div>Loading users...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Allowed Tabs</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>{u.githubUsername}</td>
                <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                <td>{u.allowedTabs?.join(', ')}</td>
                <td>
                  <button className="btn btn-sm" onClick={() => handleEdit(u)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(u)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default Admin
